import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import React from 'react';

interface WifiNetwork {
  ssid: string;
  password: string;
}

export interface OwnerFormData {
  id?: number;
  name: string;
  default_config: string | null;
  wifi_networks?: WifiNetwork[];
  notes?: string | null;
  api_token?: string | null;
  server_host?: string | null;
  country_code?: string | null;
}

interface CreateOwnerDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: OwnerFormData) => void;
  editData?: OwnerFormData | null;
}

function tryFormatJson(text: string): string {
  try { return JSON.stringify(JSON.parse(text), null, 4); }
  catch { return text; }
}

function validateJson(text: string): string | null {
  if (!text.trim()) return null;
  try { JSON.parse(text); return null; }
  catch (e) { return (e as Error).message; }
}

function CreateOwnerDialog({ open, onClose, onSave, editData }: CreateOwnerDialogProps) {
  const [name, setName] = React.useState('');
  const [config, setConfig] = React.useState('');
  const [jsonError, setJsonError] = React.useState<string | null>(null);
  const [notes, setNotes] = React.useState('');
  const [apiToken, setApiToken] = React.useState('');
  const [serverHost, setServerHost] = React.useState('iot.ctrnd.com');
  const [countryCode, setCountryCode] = React.useState('CA');
  const [showApiToken, setShowApiToken] = React.useState(false);
  const [wifiNetworks, setWifiNetworks] = React.useState<WifiNetwork[]>([]);
  const [showPasswords, setShowPasswords] = React.useState<Set<number>>(new Set());

  React.useEffect(() => {
    if (editData) {
      setName(editData.name);
      setConfig(editData.default_config ? tryFormatJson(editData.default_config) : '');
      setNotes(editData.notes || '');
      setApiToken(editData.api_token || '');
      setServerHost(editData.server_host || 'iot.ctrnd.com');
      setCountryCode(editData.country_code || 'CA');
      setWifiNetworks(editData.wifi_networks || []);
    } else {
      resetForm();
    }
  }, [editData, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name && !jsonError) {
      onSave({
        name,
        default_config: config.trim() || null,
        wifi_networks: wifiNetworks.filter(w => w.ssid.trim()),
        notes: notes.trim() || null,
        api_token: apiToken.trim() || null,
        server_host: serverHost.trim() || 'iot.ctrnd.com',
        country_code: countryCode.trim().toUpperCase() || 'CA',
      });
      resetForm();
    }
  };

  const resetForm = () => {
    setName('');
    setConfig('');
    setNotes('');
    setApiToken('');
    setServerHost('iot.ctrnd.com');
    setCountryCode('CA');
    setShowApiToken(false);
    setJsonError(null);
    setWifiNetworks([]);
    setShowPasswords(new Set());
  };

  const addWifi = () => setWifiNetworks([...wifiNetworks, { ssid: '', password: '' }]);
  const removeWifi = (i: number) => setWifiNetworks(wifiNetworks.filter((_, idx) => idx !== i));
  const moveWifi = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= wifiNetworks.length) return;
    const updated = [...wifiNetworks];
    [updated[i], updated[j]] = [updated[j], updated[i]];
    setWifiNetworks(updated);
  };
  const updateWifi = (i: number, field: keyof WifiNetwork, value: string) => {
    const updated = [...wifiNetworks];
    updated[i] = { ...updated[i], [field]: value };
    setWifiNetworks(updated);
  };
  const togglePassword = (i: number) => {
    const next = new Set(showPasswords);
    if (next.has(i)) next.delete(i); else next.add(i);
    setShowPasswords(next);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFormat = () => {
    if (jsonError) return;
    setConfig(tryFormatJson(config));
  };

  const textAreaStyle: React.CSSProperties = {
    width: '100%',
    height: '200px',
    padding: '16px',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    resize: 'vertical',
    overflow: 'auto',
    backgroundColor: '#2d2d2e',
    color: '#E5E5E5',
    border: `2px solid ${jsonError ? '#f44336' : '#4a4a4a'}`,
    borderRadius: '4px',
    boxSizing: 'border-box' as const
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{editData ? 'Edit Owner' : 'Create Owner'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus={!editData}
            required
            margin="dense"
            label="Name"
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            size="small"
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes about this owner..."
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <TextField
              margin="dense"
              label="API Token"
              size="small"
              type={showApiToken ? 'text' : 'password'}
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              placeholder="Device auth token"
              sx={{ flex: 2 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton size="small" onClick={() => setShowApiToken(!showApiToken)} edge="end">
                      {showApiToken ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  )
                }
              }}
            />
            <TextField
              margin="dense"
              label="Server Host"
              size="small"
              value={serverHost}
              onChange={(e) => setServerHost(e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              margin="dense"
              label="Country"
              size="small"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              sx={{ width: 80 }}
            />
          </Box>

          <Box sx={{ mt: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">WiFi Networks</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addWifi}>Add</Button>
            </Box>
            {wifiNetworks.map((wifi, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="SSID"
                  value={wifi.ssid}
                  onChange={(e) => updateWifi(i, 'ssid', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  placeholder="Password"
                  type={showPasswords.has(i) ? 'text' : 'password'}
                  value={wifi.password}
                  onChange={(e) => updateWifi(i, 'password', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => togglePassword(i)} title="Show/hide password">
                  {showPasswords.has(i) ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small" onClick={() => moveWifi(i, -1)} disabled={i === 0} title="Move up (higher priority)">
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => moveWifi(i, 1)} disabled={i === wifiNetworks.length - 1} title="Move down (lower priority)">
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => removeWifi(i)} title="Remove">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            {wifiNetworks.length === 0 && (
              <Typography variant="caption" color="text.secondary">No WiFi — device will use captive portal</Typography>
            )}
          </Box>

          <Box sx={{ position: 'relative', mt: 2, mb: 1 }}>
            <Box
              component="label"
              sx={{
                position: 'absolute',
                top: '-8px',
                left: '10px',
                bgcolor: 'background.paper',
                px: 1,
                fontSize: '0.75rem',
                color: 'text.primary',
                zIndex: 1
              }}
            >
              Default Config
            </Box>
            <textarea
              style={textAreaStyle}
              value={config}
              onChange={(e) => {
                setConfig(e.target.value);
                setJsonError(validateJson(e.target.value));
              }}
            />
            {jsonError && (
              <Typography sx={{ color: '#f44336', fontSize: '0.75rem', mt: 0.5 }}>
                {jsonError}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              disabled={!!jsonError || !config.trim()}
              onClick={handleFormat}
              sx={{
                color: '#81c784',
                borderColor: '#81c784',
                '&:hover': {
                  borderColor: '#81c784',
                  backgroundColor: 'rgba(129, 199, 132, 0.1)'
                }
              }}
            >
              Format
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!jsonError}>
            {editData ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CreateOwnerDialog;
